// libraries
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import { Link, useParams } from 'react-router-dom'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { Box, Button, Typography, Stack, Tooltip, IconButton } from '@mui/material'
import TimerSharpIcon from '@mui/icons-material_modern/TimerSharp'
import LockIcon from '@mui/icons-material_modern/Lock'
import DeleteIcon from '@mui/icons-material_modern/Delete'
import { makeStyles } from '@mui/styles'
import { union } from 'lodash'

// generic components
import InboundTicketsModal from 'components/generic/modals/dialog-modal/InboundTicketsModal'
import FreightBillsModal from 'components/generic/modals/dialog-modal/FreightBillsModal'
import BasicDataGrid from 'components/generic/datagrid'

// helpers and utils
import { TICKET_STATUSES, BILL_TYPES, MATERIAL_TYPE_RULES } from 'utils/constants'
import { setDecimalPrecision, checkIfDateIsLocked, generateLockTooltip } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { aggregateOBTMaterials, materialsHasTareRequiredAndMissing } from 'components/containers/bills/utils'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { paymentHoldInEffect } from 'components/containers/inbound-tickets/helpers/grid-validations'
import Big from 'big.js'

const pinnedRows = ['subtotal', 'tax', 'total']
const excludePinnedRowsSort = (v1, v2, row1, row2) => {
  const isPinned1 = pinnedRows.includes(row1.id)
  const isPinned2 = pinnedRows.includes(row2.id)
  if (isPinned1 || isPinned2) return 0
  return v1 - v2 // regular sort
}

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '&.cell-error': {
        backgroundColor: theme.palette.error.background,
        color: theme.palette.error.main,
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        fontWeight: '500',
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&.subtotal-row': {
        fontWeight: '500',
      },
      '&.tax-row': {
        fontWeight: '500',
      },
      '&.normal-row': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiCircularProgress-root': {
      zIndex: 1,
    },
  },
}))

const BillTicketsTable = ({
  billData,
  billType,
  customerData,
  selectedTicketsData,
  setSelectedTicketsData,
  setSelectedCustomerId,
  getInboundTicketsByIds,
  getOutboundTicketsByIds,
  isLoadingTicketsByIds,
  setHasEditedInboundTickets,
  setErrorMessage,
  paymentMade,
  billIsFullyPaid,
  billIsInvoiced,
  getValues,
  totalNet,
  subtotalValue,
  taxValue,
  totalValue,
  pricePrepend,
  setHasLockedTickets,
}) => {
  const apiRef = useGridApiRef()
  const classes = useStyles()
  const [openAddModal, setOpenAddModal] = useState(false)
  const { id: billId } = useParams()
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const { enableMaterialPaymentHolds, enableLockAccountingV2 } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const { getDueDateFormatted } = usePaymentTerms({})
  const { mergeTickets: mergeTicketsState } = useSelector(state => state.bill)

  useEffect(() => {
    if (selectedTicketsData.length > 0 && apiRef?.current?.updateRows && isLoadingTicketsByIds === false) {
      apiRef?.current?.updateRows([
        { id: 'subtotal', _action: 'delete' },
        { id: 'tax', _action: 'delete' },
        { id: 'total', _action: 'delete' },
      ])
      setTimeout(() => {
        const newRows = [
          {
            id: 'subtotal',
            ticketId: 'Subtotal',
            netWeight: totalNet,
            value: subtotalValue,
          },
          {
            id: 'tax',
            ticketId: 'Tax',
            value: taxValue,
          },
          {
            id: 'total',
            ticketId: 'Total',
            netWeight: totalNet,
            value: totalValue,
          },
        ]
        apiRef?.current?.updateRows(taxValue === 0 ? newRows.slice(2) : newRows)
      }, 500)
    }
  }, [apiRef, totalNet, totalValue, isLoadingTicketsByIds, billType])

  const handleRemoveTicket = useCallback(
    ticketId => {
      const updatedTickets = selectedTicketsData.filter(ticket => ticket.id !== ticketId)
      setSelectedTicketsData(updatedTickets)
      setHasEditedInboundTickets(true)
    },
    [selectedTicketsData, setSelectedTicketsData, setHasEditedInboundTickets],
  )

  const materialBillColumns = useMemo(
    () => [
      {
        field: 'ticketId',
        headerName: 'Ticket #',
        width: 150,
        sortable: true,
        valueGetter: params => {
          return params.row.ticketId
        },
        sortComparator: excludePinnedRowsSort,
        renderCell: params => {
          const { id, ticketId, effectiveDate, materials, isInLockedAccountingPeriodV2 } = params.row
          if (pinnedRows.includes(id)) return ticketId
          const displayPaymentHold = materials?.some(material => {
            return (
              material.materialType?.rules?.some(rule => rule.type === MATERIAL_TYPE_RULES.PAYMENT_HOLD) &&
              paymentHoldInEffect(material.materialType?.rules, effectiveDate)
            )
          })
          return (
            <>
              <Link to={`/app/purchases/ticket-library/${id}`} target="_blank">
                {ticketId}
              </Link>
              {enableMaterialPaymentHolds && displayPaymentHold && (
                <Tooltip title="This ticket is on payment hold">
                  <TimerSharpIcon color="warning" sx={{ ml: 1 }} />
                </Tooltip>
              )}
              {isInLockedAccountingPeriodV2 && (
                <Tooltip title={generateLockTooltip('inbound ticket')} placement="top">
                  <LockIcon color="info" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </>
          )
        },
      },
      {
        field: 'date',
        headerName: 'Date Created',
        width: 150,
        sortable: false,
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { customerName, customerId } = params.row
          return customerId && customerName ? (
            <Link to={`/app/customers/${customerId}/account`} target="_blank">
              {customerName}
            </Link>
          ) : null
        },
      },
      {
        field: 'orderNumber',
        headerName: 'PO#',
        width: 100,
        sortable: true,
        valueGetter: params => {
          const { transportationInfo } = params.row
          return Big(transportationInfo?.purchaseOrderNumber || 0).toNumber()
        },
        sortComparator: excludePinnedRowsSort,
        renderCell: params => {
          const { purchaseOrderNumber, purchaseOrderId } = params.row.transportationInfo || {}
          if (pinnedRows.includes(params.row.id)) return ''
          if (!purchaseOrderNumber || !purchaseOrderId) return '-'
          return (
            <Link to={`/app/contracts/purchase-orders/${purchaseOrderId}`} target="_blank">
              {purchaseOrderNumber}
            </Link>
          )
        },
      },
      {
        field: 'statusValue',
        headerName: 'Status',
        width: 150,
        sortable: false,
      },
      {
        field: 'netWeight',
        headerName: 'Net Weight',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { id, netWeight } = params.row
          if (id === 'tax') return ''
          return `${setDecimalPrecision(netWeight, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })} ${defaultYardUm}`
        },
        sortable: false,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        width: 150,
        sortable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params =>
          `${pricePrepend}${setDecimalPrecision(params.row.value || 0, { decimalPrecisionOverride: 2 })}`,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const { id } = params.row
          // Don't show delete button for summary rows or when payment is made/bill is paid/bill is invoiced
          if (pinnedRows.includes(id) || paymentMade || billIsFullyPaid || billIsInvoiced) {
            return null
          }
          return (
            <Tooltip title="Remove ticket from bill">
              <IconButton
                size="small"
                onClick={() => handleRemoveTicket(id)}
                color="error"
                data-test={`remove-ticket-${id}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        },
      },
    ],
    [
      selectedTicketsData,
      pricePrepend,
      defaultYardUm,
      paymentMade,
      billIsFullyPaid,
      billIsInvoiced,
      handleRemoveTicket,
    ],
  )

  const freightBillColumns = useMemo(
    () => [
      {
        field: 'ticketId',
        headerName: 'Ticket #',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { id, ticketId, ticketType, isInLockedAccountingPeriodV2 } = params.row
          if (pinnedRows.includes(id)) return ticketId
          return (
            <>
              <Link
                to={`/app/${ticketType === 'IBT' ? 'purchases/ticket-library' : 'sales/outbound-tickets'}/${id}`}
                target="_blank"
              >
                {ticketId}
              </Link>
              {isInLockedAccountingPeriodV2 && (
                <Tooltip title={generateLockTooltip('freight bill')} placement="top">
                  <LockIcon color="info" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </>
          )
        },
      },
      {
        field: 'orderNumber',
        headerName: 'Order #',
        flex: 1,
        sortable: false,
      },
      {
        field: 'materials',
        headerName: 'Material',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { materialTypeCommonNames } = params.row
          return (
            <Tooltip title={materialTypeCommonNames?.join(', ')}>
              <div>{materialTypeCommonNames?.length > 1 ? 'Mixed' : materialTypeCommonNames?.[0] || ''}</div>
            </Tooltip>
          )
        },
      },
      {
        field: 'netWeight',
        headerName: 'Net Weight',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { id, netWeight } = params.row
          if (id === 'tax') return ''
          return `${setDecimalPrecision(netWeight, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })} ${defaultYardUm}`
        },
        sortable: false,
      },
      {
        field: 'freightCost',
        headerName: 'Freight Cost',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params =>
          `${pricePrepend}${setDecimalPrecision(params.row?.value || 0, { decimalPrecisionOverride: 2 })}`,
        sortable: false,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 1,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const { id } = params.row
          // Don't show delete button for summary rows or when payment is made/bill is paid/bill is invoiced
          if (pinnedRows.includes(id) || paymentMade || billIsFullyPaid || billIsInvoiced) {
            return null
          }
          return (
            <Tooltip title="Remove freight bill">
              <IconButton
                size="small"
                onClick={() => handleRemoveTicket(id)}
                color="error"
                data-test={`remove-ticket-${id}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        },
      },
    ],
    [
      selectedTicketsData,
      pricePrepend,
      defaultYardUm,
      paymentMade,
      billIsFullyPaid,
      billIsInvoiced,
      handleRemoveTicket,
    ],
  )

  const getPaymentTerms = useCallback(
    data => data.customer?.defaultPaymentTermId || data.defaultPaymentTermId,
    [customerData],
  )

  const fetchTickets = useCallback(
    async (inboundTicketIds, outboundTicketIds, initialLoad = false) => {
      try {
        const formattedTickets = []

        let someTicketsAreLocked

        if (inboundTicketIds?.length > 0) {
          const { data: selectedInboundTicketsData, error: getSelectedInboundTicketsError } =
            await getInboundTicketsByIds({
              body: inboundTicketIds,
            })
          if (getSelectedInboundTicketsError) throw new Error(getSelectedInboundTicketsError?.data?.error)

          selectedInboundTicketsData?.Items?.forEach(ticket => {
            let feeOrPromotionAmount = 0
            if (ticket?.status?.value === TICKET_STATUSES.PAID.id) {
              feeOrPromotionAmount = ticket.feeOrPromotion?.reduce((acc, feeOrPromotion) => {
                const amountToAdd = feeOrPromotion.applied ? feeOrPromotion.calculatedValue : 0
                return (acc += amountToAdd)
              }, 0)
            } else {
              feeOrPromotionAmount = ticket.feeOrPromotion?.reduce((acc, feeOrPromotion) => {
                return (acc += feeOrPromotion?.calculatedValue || 0)
              }, 0)
            }
            const materialSubTotal =
              billType === BILL_TYPES.MATERIAL.id ? Dinero(ticket.netValue)?.toUnit() : ticket.freightCost?.cost || 0
            const subTotal = materialSubTotal + feeOrPromotionAmount
            const paymentTerms =
              billType === BILL_TYPES.MATERIAL.id ? getPaymentTerms(ticket) : customerData?.defaultPaymentTermId

            let isInLockedAccountingPeriodV2
            if (!enableLockAccountingV2) {
              isInLockedAccountingPeriodV2 = false
            } else if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) {
              isInLockedAccountingPeriodV2 = false
            } else {
              isInLockedAccountingPeriodV2 = checkIfDateIsLocked(lockAccountingPeriod, ticket.effectiveDate)
            }
            if (isInLockedAccountingPeriodV2) {
              someTicketsAreLocked = true
            }

            formattedTickets.push({
              ...ticket,
              address:
                billType === BILL_TYPES.MATERIAL.id
                  ? customerData?.locations?.find(location => location.id === ticket.locationId)
                  : undefined,
              date: dateFormatter(ticket.effectiveDate),
              customerName: ticket.customer?.customerCommonIdentifierString,
              customerId: ticket.customer?.id,
              statusValue: TICKET_STATUSES[ticket.status?.value]?.name,
              dueDate: getDueDateFormatted(ticket.effectiveDate, paymentTerms),
              subTotal,
              value: subTotal,
              gross: ticket.loadWeight?.gross,
              tare: ticket.loadWeight?.tare,
              adjustments: ticket.loadWeight?.deductions,
              orderNumber: ticket.transportationInfo?.purchaseOrderNumber || '-',
              billOfLading: ticket?.transportationInfo?.billOfLading,
              materialTypeCommonNames:
                ticket?.materials?.map(material => material.materialType?.commonName || '') || [],
              ticketType: 'IBT',
              isInLockedAccountingPeriodV2,
            })
          })
        }

        if (outboundTicketIds?.length > 0) {
          const { data: selectedOutboundTicketsData, error: getSelectedOutboundTicketsError } =
            await getOutboundTicketsByIds({
              body: outboundTicketIds,
            })
          if (getSelectedOutboundTicketsError) throw new Error(getSelectedOutboundTicketsError?.data?.error)

          selectedOutboundTicketsData?.Items?.forEach(ticket => {
            const paymentTerms = customerData?.defaultPaymentTermId
            const { gross, tare, materialCommonNames } = aggregateOBTMaterials(ticket)

            let isInLockedAccountingPeriodV2
            if (!enableLockAccountingV2) {
              isInLockedAccountingPeriodV2 = false
            } else if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) {
              isInLockedAccountingPeriodV2 = false
            } else {
              isInLockedAccountingPeriodV2 = checkIfDateIsLocked(lockAccountingPeriod, ticket.effectiveDate)
            }
            if (isInLockedAccountingPeriodV2) {
              someTicketsAreLocked = true
            }

            formattedTickets.push({
              ...ticket,
              ticketId: ticket.tag,
              date: dateFormatter(ticket.effectiveDate),
              customerName: ticket.customerDisplayName,
              customerId: ticket.customerId,
              statusValue: TICKET_STATUSES[ticket.status]?.name,
              dueDate: getDueDateFormatted(ticket.effectiveDate, paymentTerms),
              subTotal: ticket.freightCost?.cost || 0,
              value: ticket.freightCost?.cost || 0,
              gross,
              tare,
              orderNumber: ticket.salesOrder?.salesOrderTicketId || '-',
              billOfLading: '',
              materialTypeCommonNames: materialCommonNames.filter(name => !!name),
              ticketType: 'OBT',
              isInLockedAccountingPeriodV2,
            })
          })
        }

        setHasLockedTickets(someTicketsAreLocked)

        const sortedTickets = formattedTickets.sort((a, b) => {
          const ticketIdA = isNaN(Number(a.ticketId)) ? a.ticketId : Number(a.ticketId)
          const ticketIdB = isNaN(Number(b.ticketId)) ? b.ticketId : Number(b.ticketId)
          if (ticketIdA < ticketIdB) return -1
          if (ticketIdA > ticketIdB) return 1
          return 0
        })

        setSelectedTicketsData(sortedTickets)

        if (billType === BILL_TYPES.MATERIAL.id) {
          if (!initialLoad) {
            setSelectedCustomerId(sortedTickets?.[0]?.customer?.id)
          }
        } else if (billType === BILL_TYPES.FREIGHT.id) {
          if (!initialLoad) {
            setSelectedCustomerId(sortedTickets?.[0]?.carrierId)
          }
        }
      } catch (error) {
        setSelectedTicketsData([])
        setErrorMessage(error?.data?.error || error?.message || 'An error occurred while fetching the ticket data')
      }
    },
    [billType, customerData, enableLockAccountingV2],
  )

  useEffect(() => {
    if (billData && customerData && mergeTicketsState) {
      if (billData?.inboundTicketIds?.length > 0 || billData?.outboundTicketIds?.length > 0) {
        let ibtIds = billData?.inboundTicketIds
        if (mergeTicketsState?.shouldMergeTickets) {
          ibtIds = union(billData?.inboundTicketIds, mergeTicketsState?.ibtIds)
          setHasEditedInboundTickets(true)
        }
        fetchTickets(ibtIds, billData?.outboundTicketIds, true)
      } else {
        setSelectedTicketsData([])
      }
    }
  }, [customerData, billData, billType, mergeTicketsState])

  useEffect(() => {
    // This will be executed when is a new bill and the mergeTickets flag is true
    if (!billData && mergeTicketsState?.shouldMergeTickets) {
      fetchTickets(mergeTicketsState?.ibtIds, [])
      setHasEditedInboundTickets(true)
    }
  }, [mergeTicketsState])

  const getCellClassName = params => {
    if (
      params.field === 'ticketId' &&
      !pinnedRows.includes(params.row.id) &&
      materialsHasTareRequiredAndMissing(params.row?.materials)
    )
      return 'cell-error'
    return ''
  }

  return (
    <>
      <Stack p={3}>
        <Typography variant="h6" gutterBottom color="textSecondary">
          Tickets
        </Typography>
        <Box mt={1}>
          {!paymentMade && !billIsFullyPaid && !billIsInvoiced && (
            <Button
              data-test="add-inbound-tickets"
              variant={selectedTicketsData.length > 0 ? 'outlined' : 'contained'}
              onClick={() => {
                setOpenAddModal(true)
              }}
              disabled={isLoadingTicketsByIds}
            >
              {`${selectedTicketsData.length > 0 ? 'ADD/REMOVE' : 'ADD'} ${billType === BILL_TYPES.MATERIAL.id ? 'TICKETS' : 'FREIGHT BILLS'}`}
            </Button>
          )}
          {billData?.inboundTicketIds?.length > 0 ||
          billData?.outboundTicketIds?.length > 0 ||
          selectedTicketsData.length > 0 ? (
            <Box mt={3}>
              <BasicDataGrid
                className={classes.root}
                density="compact"
                columns={billType === BILL_TYPES.FREIGHT.id ? freightBillColumns : materialBillColumns}
                rows={selectedTicketsData}
                apiRef={apiRef}
                disableColumnMenu
                hideFooter
                getRowClassName={params => (pinnedRows.includes(params.row.id) ? `${params.row.id}-row` : 'normal-row')}
                getCellClassName={params => getCellClassName(params)}
                pinnedRows={{
                  bottom: [{ id: 'subtotal' }, { id: 'tax' }, { id: 'total' }],
                }}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'ticketId', sort: 'asc' }],
                  },
                }}
                onSortModelChange={model => {
                  if (model.length === 0) {
                    apiRef?.current?.setSortModel([{ field: 'ticketId', sort: 'asc' }])
                  }
                }}
              />
            </Box>
          ) : (
            <Typography align="center" color="text.secondary" mt={5} fullwidth="true">
              No tickets have been added
            </Typography>
          )}
        </Box>
      </Stack>
      {openAddModal && billType === BILL_TYPES.MATERIAL.id && (
        <InboundTicketsModal
          title={`Import Tickets to Bill`}
          buttonTitle="IMPORT TICKETS"
          selectedList={selectedTicketsData.map(({ id }) => id)}
          open={openAddModal}
          onAdd={ticketIds => {
            setOpenAddModal(false)
            setHasEditedInboundTickets(true)
            fetchTickets(ticketIds, [])
          }}
          onClose={() => setOpenAddModal(false)}
          billIds={[billId]}
          currentCustomer={getValues('customer.name')}
          getPaymentTerms={getPaymentTerms}
          currentCurrency={billData?.currency}
        />
      )}
      {openAddModal && billType === BILL_TYPES.FREIGHT.id && (
        <FreightBillsModal
          title={`Import Freight Bills`}
          buttonTitle="IMPORT FREIGHT BILLS"
          selectedList={selectedTicketsData.map(({ id }) => id)}
          selectedInboundList={selectedTicketsData.filter(ticket => ticket.ticketType === 'IBT').map(({ id }) => id)}
          selectedOutboundList={selectedTicketsData.filter(ticket => ticket.ticketType === 'OBT').map(({ id }) => id)}
          open={openAddModal}
          onAdd={(inboundTicketIds, outboundTicketIds) => {
            setOpenAddModal(false)
            setHasEditedInboundTickets(true)
            fetchTickets(inboundTicketIds, outboundTicketIds)
          }}
          onClose={() => setOpenAddModal(false)}
          billId={billId}
          currentCarrier={getValues('customer.name')}
          getPaymentTerms={getPaymentTerms}
        />
      )}
    </>
  )
}

BillTicketsTable.propTypes = {
  billData: PropTypes.object,
  billType: PropTypes.oneOf([BILL_TYPES.MATERIAL.id, BILL_TYPES.FREIGHT.id]).isRequired,
  customerData: PropTypes.object,
  selectedTicketsData: PropTypes.array,
  setSelectedTicketsData: PropTypes.func,
  setSelectedCustomerId: PropTypes.func,
  getInboundTicketsByIds: PropTypes.func.isRequired,
  getOutboundTicketsByIds: PropTypes.func.isRequired,
  isLoadingTicketsByIds: PropTypes.bool,
  setHasEditedInboundTickets: PropTypes.func,
  setErrorMessage: PropTypes.func,
  paymentMade: PropTypes.bool,
  billIsFullyPaid: PropTypes.bool,
  billIsInvoiced: PropTypes.bool,
  getValues: PropTypes.func,
  watch: PropTypes.func,
  totalNet: PropTypes.number,
  subtotalValue: PropTypes.number,
  taxValue: PropTypes.number,
  totalValue: PropTypes.number,
  pricePrepend: PropTypes.string,
  setHasLockedTickets: PropTypes.func,
}

BillTicketsTable.defaultProps = {
  billData: null,
  customerData: null,
  selectedTicketsData: [],
  setSelectedTicketsData: () => {},
  setSelectedCustomerId: () => {},
  isLoadingTicketsByIds: false,
  setHasEditedInboundTickets: () => {},
  setErrorMessage: () => {},
  paymentMade: false,
  billIsFullyPaid: false,
  billIsInvoiced: false,
  getValues: () => {},
  watch: () => {},
  totalNet: 0,
  subtotalValue: 0,
  taxValue: 0,
  totalValue: 0,
  pricePrepend: '$',
  setHasLockedTickets: () => {},
}

export default BillTicketsTable
